import { QueryResult } from '@apollo/client';
import { first, isEqual, isPlainObject, isUndefined, uniq } from 'lodash';
import { SnackbarKey } from 'notistack';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useFormContext } from '@/components/react-hook-form';
import { useReportError } from '@/hooks/useReportError';
import { ENTITIES_WHERE_VARIABLE } from '@/modules/entityMap/hooks/useEntityMapQuery';
import {
  getHideZeroDollarItemsChanges,
  HideZeroDollarItemsSetting,
  useTrackHideZeroDollarItemsChanges,
} from '@/modules/presentation/hooks/useTrackHideZeroDollarItemsChanges';
import {
  AugmentedUpdateClientPresentationV2Input,
  PresentationPageKind,
} from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';
import { sleep } from '@/utils/sleep';

import {
  ClientPresentationDesignerContextDataShape,
  ClientPresentationDesignerViewMode,
  useClientPresentationDesignerContext,
} from './ClientPresentationDesigner.context';
import { ClientPresentationShape } from './ClientPresentationDesigner.types';
import {
  getWaterfallWithProjections,
  NEW_BUNDLE_ID_SENTINEL,
  NEW_PAGE_ID_SENTINEL,
  SLIDE_ID_QUERY_PARAM,
} from './ClientPresentationDesigner.utils';
import { mapDataToTable } from './components/slides/ProfessionalTeamSlide/ProfessionalTeamSlide.utils';
import {
  ClientPresentationDesignerV2Query,
  ClientPresentationDesignerV2QueryVariables,
  useClientPresentationDesignerV2LazyQuery,
  useUpdateClientPresentationV2Mutation,
} from './graphql/ClientPresentationDesignerV2.generated';
import { createPresentationSavePlan } from './save/createPresentationSavePlan';
import { getWrittenBundleIds } from './save/getWrittenBundleIds';
import {
  PresentationSaveStrategy,
  ReconcileSaveResult,
} from './save/presentationSave.types';
import { usePresentationSaveQueue } from './save/session/usePresentationSaveQueue';
import { usePresentationSaveStrategy } from './save/usePresentationSaveStrategy';
import { ClientPresentationV2Bundle } from './types/ClientPresentation.PresentationBundleType';
import { ClientPresentationV2Page } from './types/ClientPresentation.PresentationPageType';

const RECONCILIATION_RETRY_DELAYS_MS = [1000, 2000];

/**
 * Applies the identities adopted by a completed save to the form as it exists
 * now. A later edit can add another bundle while the save is in flight, so the
 * response snapshot must not replace the whole current array.
 */
export function mergeReconciledBundles(
  savedBundles: ClientPresentationV2Bundle[],
  reconciledBundles: ClientPresentationV2Bundle[],
  currentBundles: ClientPresentationV2Bundle[]
): ClientPresentationV2Bundle[] {
  const reconciledBySavedId = new Map(
    savedBundles.map((bundle, index) => [
      bundle.id,
      { saved: bundle, reconciled: reconciledBundles[index] ?? bundle },
    ])
  );

  return currentBundles.map((bundle) => {
    const match = reconciledBySavedId.get(bundle.id);
    if (!match) return bundle;

    return adoptReconciledIds(match.saved, match.reconciled, bundle);
  });
}

/** Preserve newer form values while adopting identities assigned by the save. */
function adoptReconciledIds<T>(saved: T, reconciled: T, current: T): T {
  if (Array.isArray(saved)) {
    if (!Array.isArray(reconciled) || !Array.isArray(current)) return current;

    const savedIndexById = new Map(
      saved.flatMap((value, index) => {
        if (
          value !== null &&
          typeof value === 'object' &&
          'id' in value &&
          typeof value.id === 'string'
        ) {
          return [[value.id, index] as const];
        }
        return [];
      })
    );

    return current.map((value, currentIndex) => {
      const valueId =
        value !== null &&
        typeof value === 'object' &&
        'id' in value &&
        typeof value.id === 'string'
          ? value.id
          : null;
      const savedIndex = valueId ? savedIndexById.get(valueId) : currentIndex;

      return savedIndex !== undefined &&
        savedIndex < saved.length &&
        savedIndex < reconciled.length
        ? adoptReconciledIds(saved[savedIndex], reconciled[savedIndex], value)
        : value;
    }) as T;
  }

  // GraphQL scalar values such as Decimal are class instances. Treat them as
  // leaves so object spreading does not remove their prototype and methods.
  if (
    !isPlainObject(saved) ||
    !isPlainObject(reconciled) ||
    !isPlainObject(current)
  ) {
    return current;
  }

  if (
    saved === null ||
    reconciled === null ||
    current === null ||
    typeof saved !== 'object' ||
    typeof reconciled !== 'object' ||
    typeof current !== 'object'
  ) {
    return current;
  }

  const savedRecord = saved as Record<string, unknown>;
  const reconciledRecord = reconciled as Record<string, unknown>;
  const currentRecord = current as Record<string, unknown>;
  const merged = { ...reconciledRecord, ...currentRecord };

  Object.keys(currentRecord).forEach((key) => {
    if (!(key in savedRecord) || !(key in reconciledRecord)) return;

    if (key === 'id' && savedRecord[key] !== reconciledRecord[key]) {
      merged[key] = reconciledRecord[key];
      return;
    }

    merged[key] = adoptReconciledIds(
      savedRecord[key],
      reconciledRecord[key],
      currentRecord[key]
    );
  });

  return merged as T;
}

/**
 * Replaces bundle sentinels in a queued delta after an earlier save has adopted
 * their server IDs. The queue keeps each delta, but a delta planned while its
 * predecessor was in flight still addresses the identity the form held then.
 */
export function remapQueuedBundleIds(
  input: AugmentedUpdateClientPresentationV2Input,
  bundleIdRemaps: ReadonlyMap<string, string>
): AugmentedUpdateClientPresentationV2Input {
  const removeBundleIDs = input.update.removeBundleIDs?.map(
    (id) => bundleIdRemaps.get(id) ?? id
  );
  const updateBundles = input.updateBundles?.map((bundle) => ({
    ...bundle,
    id: bundleIdRemaps.get(bundle.id) ?? bundle.id,
  }));

  return {
    ...input,
    update:
      removeBundleIDs === undefined
        ? input.update
        : { ...input.update, removeBundleIDs },
    ...(updateBundles === undefined ? {} : { updateBundles }),
  };
}

/**
 * Retries with a short backoff when a post-write response does not match the
 * saved form. A read replica can remain stale for several seconds after a write.
 */
export async function refetchPresentationForReconciliation<Data>(
  refetch: () => Promise<Data | null>,
  reconcile: (data: Data) => ReconcileSaveResult
): Promise<{ data: Data; syncResult: ReconcileSaveResult } | null> {
  let data = await refetch();
  if (!data) return null;

  let syncResult = reconcile(data);
  for (const retryDelay of RECONCILIATION_RETRY_DELAYS_MS) {
    if (syncResult.kind !== 'desynced') break;

    await sleep(retryDelay);
    data = await refetch();
    if (!data) return null;
    syncResult = reconcile(data);
  }

  return { data, syncResult };
}

function getHideZeroDollarItemsSettings(
  bundles: ClientPresentationV2Bundle[]
): HideZeroDollarItemsSetting[] {
  return bundles.flatMap(({ id, waterfallOverviewBundleConfiguration }) =>
    waterfallOverviewBundleConfiguration
      ? [
          {
            bundleId: id,
            hideZeroDollarItems:
              waterfallOverviewBundleConfiguration.summaryHideZeroDollarItems,
          },
        ]
      : []
  );
}

export function usePersistPresentation(): {
  isPersistRunning: boolean;
  requestBundleDelete: (bundleId: string) => void;
} {
  const { control, setValue, getValues } =
    useFormContext<ClientPresentationShape>();
  const {
    presentationId,
    householdId,
    isPersistRunning,
    setPersistRunning,
    setIsWorkflowGenerating,
    contextData,
    setContextData,
    saveState,
    refetchPresentation,
  } = useClientPresentationDesignerContext();
  const [bundles, presentationConfiguration, title] = useWatch({
    control,
    name: ['bundles', 'presentationConfiguration', 'title'],
  });

  const { showFeedback, hideFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { navigateToSlide } = useNavigateToSlide();
  const strategy = usePresentationSaveStrategy();
  const trackHideZeroDollarItemsChanges =
    useTrackHideZeroDollarItemsChanges('presentation');
  const { requestSave } = usePresentationSaveQueue({
    onSaveFailed: ({ bundleIds, error, retry }) => {
      reportError(
        `A presentation save failed after a retry [${presentationId}] for bundles [${bundleIds.join(', ')}]`,
        error as Error,
        {
          presentation_save_strategy: strategy.type,
          presentation_save_failure: 'retry_exhausted',
        }
      );
      showFeedback(
        'Some changes could not be saved. Your edits are still available.',
        {
          variant: 'error',
          persist: true,
          alertActionButton: {
            label: 'Retry',
            buttonProps: { onClick: retry },
          },
        }
      );
    },
  });
  const missingHeaderToastRef = useRef<{
    key: SnackbarKey;
    navId: string;
  } | null>(null);

  const {
    estateWaterfallBundleConfigurationMap,
    entityDetailMap,
    presentationConfiguration: contextPresentationConfiguration,
  } = contextData;
  const [updateClientPresentationV2] = useUpdateClientPresentationV2Mutation({
    // don't update state, because that'll cause a runaway re-render/re-save
    fetchPolicy: 'no-cache',
    ignoreResults: true,
    refetchQueries: [], // refetching manually -- *don't* trigger any other fetches
  });

  // this is lazy because we don't care about its output on page load, but we want the refetch
  // callback available for the after-write scenario
  const [refetch] = useClientPresentationDesignerV2LazyQuery({
    fetchPolicy: 'no-cache', // cache normalization breaks across the various page types
    variables: {
      presentationId,
      householdId,
      presentationOnly: true,
      entitiesWhere: ENTITIES_WHERE_VARIABLE,
    },
  });

  const [selectedItemId] = useWatch({ control, name: ['selectedItemId'] });

  // Form write-backs retrigger this effect. Skip one run to prevent a save loop;
  // save state covers ID sync, but not the later `pendingGenerate` cleanup.
  const skipNextPersistRef = useRef(false);

  // Latest context data, for saves that run after the render they were planned
  // in. Assigned during render rather than in an effect so a save that starts in
  // the same tick still reads the current value.
  const contextDataRef = useRef(contextData);
  contextDataRef.current = contextData;

  // A queued delta can name a bundle by the sentinel it had when the delta was
  // planned. Keep each adopted identity until every later delta can address the
  // server row instead.
  const bundleIdRemapsRef = useRef(new Map<string, string>());

  const requestBundleDelete = useCallback(
    (bundleId: string): void => {
      const input = strategy.buildDeleteBundleInput?.(bundleId, {
        householdId,
        presentationId,
      });
      if (!input) return;

      const currentBundles = getValues('bundles');
      const sentState = {
        bundles: currentBundles.filter((bundle) => bundle.id !== bundleId),
        presentationConfiguration: getValues('presentationConfiguration'),
        title: getValues('title'),
      };
      requestSave({
        // Reindexing every surviving bundle depends on this deletion.
        bundleIds: currentBundles.map((bundle) => bundle.id),
        run: async () => {
          setPersistRunning(true);
          try {
            const { data } = await updateClientPresentationV2({
              variables: {
                input: remapQueuedBundleIds(input, bundleIdRemapsRef.current),
              },
            });
            if (!data?.updateClientPresentationV2?.id) {
              throw new Error(
                'the bundle deletion returned no presentation id'
              );
            }

            const currentState = {
              bundles: getValues('bundles'),
              presentationConfiguration: getValues('presentationConfiguration'),
              title: getValues('title'),
            };
            if (isEqual(currentState, sentState)) {
              saveState.advance(sentState);
            }
          } finally {
            setPersistRunning(false);
          }
        },
      });
    },
    [
      getValues,
      householdId,
      presentationId,
      requestSave,
      saveState,
      setPersistRunning,
      strategy,
      updateClientPresentationV2,
    ]
  );

  useEffect(() => {
    if (skipNextPersistRef.current) {
      skipNextPersistRef.current = false;
      return;
    }

    const plan = createPresentationSavePlan({
      baseline: saveState.read(),
      current: { bundles, presentationConfiguration, title },
      context: { householdId, presentationId },
      strategy,
    });

    if (plan.kind === 'skip') {
      return;
    }

    if (plan.kind === 'blocked') {
      const { missingHeaderNavId } = plan;
      // Dismiss a stale toast if the offending slide changed.
      if (
        missingHeaderToastRef.current &&
        missingHeaderToastRef.current.navId !== missingHeaderNavId
      ) {
        hideFeedback(missingHeaderToastRef.current.key);
        missingHeaderToastRef.current = null;
      }

      if (!missingHeaderToastRef.current) {
        const key = showFeedback(
          'Custom slides require a title. Add a title to save.',
          {
            variant: 'error',
            persist: true,
            alertActionButton: {
              label: 'Go to slide',
              buttonProps: {
                onClick: () => navigateToSlide(missingHeaderNavId),
              },
            },
          }
        );
        missingHeaderToastRef.current = { key, navId: missingHeaderNavId };
      }
      return;
    }

    // Reset once all custom pages have valid titles; dismiss only the missing-title toast
    if (missingHeaderToastRef.current) {
      hideFeedback(missingHeaderToastRef.current.key);
      missingHeaderToastRef.current = null;
    }

    const { input, pendingGenerateBundleIds, sentState } = plan;
    // A save plan needs a baseline, so the empty fallback never applies.
    const hideZeroDollarItemsChanges = getHideZeroDollarItemsChanges(
      getHideZeroDollarItemsSettings(saveState.read()?.bundles ?? []),
      getHideZeroDollarItemsSettings(sentState.bundles)
    );

    // Advance before send so intervening renders do not queue the same edit.
    saveState.advance(plan.sentState);

    async function persistData(
      saveInput: AugmentedUpdateClientPresentationV2Input
    ) {
      setPersistRunning(true);
      let mutationSucceeded = false;
      // Incomplete cycles invalidate the optimistic server state.
      let cycleCompleted = false;
      let threw = false;
      let saveStage: 'mutation' | 'reconcile' | 'refetch' = 'mutation';
      /*
       * Because there is a **massive** performance difference in loading waterfalls
       * after write vs. in a separate query (>10s vs <500ms), chain into a separate read
       */

      try {
        // write
        const { data: mutationData } = await updateClientPresentationV2({
          variables: { input: saveInput },
        });

        // The write did not land, so this is worth retrying: nothing reached the
        // server for a second attempt to duplicate.
        if (!mutationData?.updateClientPresentationV2?.id) {
          throw new Error('the presentation save returned no id');
        }
        mutationSucceeded = true;
        trackHideZeroDollarItemsChanges(hideZeroDollarItemsChanges);

        /*
         * Because there is a **massive** performance difference in loading
         * waterfalls after write vs. in a separate query (>10s vs <500ms),
         * this is a separate read rather than a fragment on the mutation.
         */
        const reconciliation = await refetchPresentationForReconciliation(
          async () => {
            saveStage = 'refetch';
            const { data } = await refetch();
            return data ?? null;
          },
          (data) => {
            saveStage = 'reconcile';
            return strategy.reconcilePresentation({
              bundles: sentState.bundles,
              selectedItemId: getValues('selectedItemId'),
              responseData: data,
              context: { householdId, presentationId },
            });
          }
        );
        if (!reconciliation) {
          return;
        }
        const { data, syncResult } = reconciliation;

        // Sync the form before rebuilding the context maps, not after. The sync
        // is what swaps a new bundle's sentinel id for the real one, and the maps
        // are keyed by the id the form holds — so rebuilding first would file
        // that bundle's data under an id the form is about to replace.
        //
        // Reconcile the snapshot this response describes. The form can contain a
        // later edit by now, including another new bundle, so correlating the
        // response against current state would reject a valid response merely
        // because their bundle counts differ.
        let reconciledBundles = getValues('bundles');
        if (syncResult.kind === 'synced') {
          reconciledBundles = mergeReconciledBundles(
            sentState.bundles,
            syncResult.updatedBundles,
            reconciledBundles
          );
          sentState.bundles.forEach((savedBundle, index) => {
            const reconciledId = syncResult.updatedBundles[index]?.id;
            if (reconciledId && reconciledId !== savedBundle.id) {
              bundleIdRemapsRef.current.set(savedBundle.id, reconciledId);
            }
          });
          skipNextPersistRef.current = true;
          setValue('bundles', reconciledBundles);
          if (syncResult.newSelectedId) {
            setValue('selectedItemId', syncResult.newSelectedId);
          }
        }

        rebuildContextData({
          data,
          // Read at run time, not from the closure this save was planned in. The
          // queue can start a save well after it was planned, and rebuilding the
          // maps from a snapshot taken then would revert whatever an earlier save
          // added to them.
          contextData: contextDataRef.current,
          setContextData,
          bundles: reconciledBundles,
          resolveContextMapKey: strategy.resolveContextMapKey,
        });

        // A desync invalidates save state below. Refetch must seed it before the
        // next save can run.
        if (syncResult.kind === 'desynced') {
          reportError(
            'A presentation save response could not be reconciled',
            new Error(syncResult.reason),
            {
              presentation_save_strategy: strategy.type,
              presentation_save_failure: 'reconciliation_desync',
            }
          );
          return;
        }

        // Store reconciled form state, including adopted server IDs.
        cycleCompleted = true;
        saveState.advance({
          bundles: getValues('bundles'),
          presentationConfiguration: getValues('presentationConfiguration'),
          title: getValues('title'),
        });
      } catch (error) {
        // A post-write failure must not replay a mutation that already landed.
        // Recover below without retrying the write.
        threw = !mutationSucceeded;
        reportError(
          `Caught error when updating presentation [${presentationId}]`,
          error as Error,
          {
            presentation_save_strategy: strategy.type,
            presentation_save_failure: `${saveStage}_failed`,
          }
        );
        if (threw) {
          // The queue owns retrying writes that did not reach the backend.
          throw error;
        }
      } finally {
        setPersistRunning(false);

        // Do not retry a payload that may have landed. When the form still
        // matches this save, refetch to restore authoritative state. Preserve a
        // newer local edit: resetting from a stale replica could discard it,
        // and its queued save can advance state after this request completes.
        if (!cycleCompleted && !threw) {
          const currentState = {
            bundles: getValues('bundles'),
            presentationConfiguration: getValues('presentationConfiguration'),
            title: getValues('title'),
          };
          if (isEqual(currentState, sentState)) {
            saveState.invalidate();
            void refetchPresentation();
          }
        }

        // Release the AI-generated overlay if the mutation never reached
        // the backend (mutation error or malformed response). On success
        // the workflow tracker drives the overlay until it completes; on
        // failure no tracker ever spawns, so the overlay would otherwise
        // be stuck. Also strip any stale `pendingGenerate` flags so the
        // next autosave doesn't re-request generation for the same
        // bundle.
        if (pendingGenerateBundleIds.length > 0) {
          const current = getValues('bundles');
          const stale = current.some(
            (b) => b.pendingGenerate && pendingGenerateBundleIds.includes(b.id)
          );
          if (stale) {
            skipNextPersistRef.current = true;
            setValue(
              'bundles',
              current.map((b) =>
                b.pendingGenerate && pendingGenerateBundleIds.includes(b.id)
                  ? { ...b, pendingGenerate: undefined }
                  : b
              )
            );
          }
          if (!mutationSucceeded) {
            setIsWorkflowGenerating(false);
          }
        }
      }
    }
    requestSave({
      bundleIds: getWrittenBundleIds(input),
      run: () =>
        persistData(remapQueuedBundleIds(input, bundleIdRemapsRef.current)),
    });
  }, [
    bundles,
    contextData,
    contextPresentationConfiguration,
    entityDetailMap,
    estateWaterfallBundleConfigurationMap,
    getValues,
    hideFeedback,
    householdId,
    navigateToSlide,
    presentationConfiguration,
    presentationId,
    refetch,
    refetchPresentation,
    reportError,
    requestSave,
    saveState,
    selectedItemId,
    setContextData,
    setIsWorkflowGenerating,
    showFeedback,
    setPersistRunning,
    setValue,
    strategy,
    title,
    trackHideZeroDollarItemsChanges,
    updateClientPresentationV2,
  ]);
  return { isPersistRunning, requestBundleDelete };
}

export function useCurrentBundleIndex(): number {
  const { control } = useFormContext<ClientPresentationShape>();
  const [bundles, selectedItemId] = useWatch({
    control,
    name: ['bundles', 'selectedItemId'],
  });
  const currentBundleIndex = useMemo(
    () =>
      bundles.findIndex(
        (bundle) =>
          bundle.id === selectedItemId ||
          bundle.pages.some((page) => page.id === selectedItemId)
      ),
    [bundles, selectedItemId]
  );

  return currentBundleIndex;
}

export function useIsDesignMode(): boolean {
  const { viewMode } = useClientPresentationDesignerContext();
  return viewMode === ClientPresentationDesignerViewMode.Designer;
}

export function useNavigateToSlide(): {
  navigateToSlide: (slideId: string) => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  const { setValue } = useFormContext<ClientPresentationShape>();

  const navigateToSlide = useCallback(
    (slideId: string) => {
      setValue('selectedItemId', slideId);

      if (
        slideId.includes(NEW_BUNDLE_ID_SENTINEL) ||
        slideId.includes(NEW_PAGE_ID_SENTINEL) ||
        slideId === searchParams.get(SLIDE_ID_QUERY_PARAM)
      ) {
        return;
      } else {
        setSearchParams(
          {
            [SLIDE_ID_QUERY_PARAM]: slideId,
          },
          {
            replace: true,
          }
        );
      }
    },
    [searchParams, setSearchParams, setValue]
  );

  return { navigateToSlide };
}

interface RelevantBundleAndPageInputBase {
  relevantBundleIndex: number | null;
  relevantBundle: ClientPresentationV2Bundle | null;
  relevantPageIndex?: number | null;
  relevantPage?: ClientPresentationV2Page | null;
  isValidBundle: boolean;
  hasPageResult: boolean;
}

interface RelevantBundleAndPageOutputWithPage
  extends RelevantBundleAndPageInputBase {
  isValidBundle: true;
  hasPageResult: true;
  relevantBundleIndex: number;
  relevantBundle: ClientPresentationV2Bundle;
  relevantPageIndex: number;
  relevantPage: ClientPresentationV2Page;
}

interface RelevantBundleAndPageOutputWithoutPage
  extends RelevantBundleAndPageInputBase {
  isValidBundle: true;
  hasPageResult: false;
  relevantBundleIndex: number;
  relevantBundle: ClientPresentationV2Bundle;
  relevantPageIndex?: null;
  relevantPage?: null;
}

interface RelevantBundleAndPageInputInvalidCase
  extends RelevantBundleAndPageInputBase {
  isValidBundle: false;
  hasPageResult: false;
  relevantBundleIndex: null;
  relevantBundle: null;
  relevantPageIndex?: null;
  relevantPage?: null;
}

/**
 * Functions that return this type have three possible outcomes:
 * - it can't find the bundle, so no data is present (`isValidBundle: false`)
 * - it finds the bundle, but no page present or no pageId provided (`hasPageResult: false`)
 * - it finds the bundle and the page, everything returned (`hasPageResult: true`)
 */
export type RelevantBundleAndPageOutput =
  | RelevantBundleAndPageOutputWithPage
  | RelevantBundleAndPageOutputWithoutPage
  | RelevantBundleAndPageInputInvalidCase;

/**
 * For a given bundle (and optional page) ID, this hook will return relevant data:
 *
 * - it can't find the bundle, so no data is present (`isValidBundle: false`)
 * - it finds the bundle, but no page present or no pageId provided (`hasPageResult: false`)
 * - it finds the bundle and the page, everything returned (`hasPageResult: true`)
 */
export function useRelevantBundleAndPage(
  bundleId: string,
  pageId?: string
): RelevantBundleAndPageOutput {
  const { control } = useFormContext<ClientPresentationShape>();
  const [bundles] = useWatch({
    control,
    name: ['bundles'],
  });

  return getRelevantBundleAndPage(bundleId, pageId, bundles);
}

/**
 * This function only exported for testing; use useRelevantBundleAndPage instead.
 */
export function getRelevantBundleAndPage(
  bundleId: string,
  pageId: string | undefined,
  bundles: ClientPresentationV2Bundle[]
): RelevantBundleAndPageOutput {
  const relevantBundleIndex = bundles.findIndex(
    (bundle) => bundle.id === bundleId
  );

  if (relevantBundleIndex === -1) {
    return {
      isValidBundle: false,
      hasPageResult: false,
      relevantBundleIndex: null,
      relevantBundle: null,
    };
  }

  const relevantBundle = bundles[relevantBundleIndex] || null;

  if (!relevantBundle) {
    return {
      isValidBundle: false,
      hasPageResult: false,
      relevantBundleIndex: null,
      relevantBundle: null,
    };
  } else if (isUndefined(pageId)) {
    return {
      isValidBundle: true,
      hasPageResult: false,
      relevantBundleIndex,
      relevantBundle,
    };
  }

  const relevantPageIndex = relevantBundle.pages.findIndex(
    (page) => page.id === pageId
  );

  if (relevantPageIndex === -1) {
    return {
      isValidBundle: true,
      hasPageResult: false,
      relevantBundleIndex,
      relevantBundle,
    };
  }

  const relevantPage = relevantBundle?.pages[relevantPageIndex] || null;

  if (!relevantPage) {
    return {
      isValidBundle: true,
      hasPageResult: false,
      relevantBundleIndex,
      relevantBundle,
    };
  }

  return {
    isValidBundle: true,
    hasPageResult: true,
    relevantBundleIndex,
    relevantBundle,
    relevantPageIndex,
    relevantPage,
  };
}

export type UpdateContextDataOutput = QueryResult<
  ClientPresentationDesignerV2Query,
  ClientPresentationDesignerV2QueryVariables
>;

interface RebuildContextDataInput {
  /** Payload from the refetch the caller already performed. */
  data: ClientPresentationDesignerV2Query;
  contextData: ClientPresentationDesignerContextDataShape;
  setContextData: (
    nextContextData: Partial<ClientPresentationDesignerContextDataShape>
  ) => void;
  bundles: ClientPresentationV2Bundle[];
  /**
   * How a response bundle is correlated to the form bundle whose context data
   * it holds. Required so both callers stay on the active strategy — a default
   * would silently keep positional correlation once a second strategy exists.
   */
  resolveContextMapKey: PresentationSaveStrategy['resolveContextMapKey'];
}

export function updateEntityDetailMap(
  entityDetailMap: ClientPresentationDesignerContextDataShape['entityDetailMap'],
  bundleId: string,
  entity: ClientPresentationDesignerContextDataShape['entityDetailMap'][string]
): ClientPresentationDesignerContextDataShape['entityDetailMap'] {
  return {
    ...entityDetailMap,
    [bundleId]: entity,
  };
}

/**
 * Files the refetched server-computed data against the form bundle it belongs
 * to.
 *
 * Callers must pass the bundles the form holds *after* any post-save sync. The
 * sync adopts the real id of a bundle that was created by this save, and the
 * slides read their context data as `map[bundle.id]` — so keying off the
 * pre-sync bundles would file that data under a sentinel id.
 */
function rebuildContextData({
  data,
  contextData,
  setContextData,
  bundles,
  resolveContextMapKey,
}: RebuildContextDataInput): void {
  const newContextData: ClientPresentationDesignerContextDataShape =
    Object.assign({}, contextData);

  const outputPresentation = first(getNodes(data.clientPresentationV2s));
  newContextData.presentationConfiguration = {
    showPageNumbers: outputPresentation?.showPageNumbers ?? false,
    showPageNumbersOnCoverSlide:
      outputPresentation?.showPageNumbersOnCoverSlide ?? false,
    pageNumberToStartFrom: outputPresentation?.pageNumberToStartFrom ?? 1,
    showLegalDisclaimer: outputPresentation?.showLegalDisclaimer ?? false,
    showClientNamesInHeader:
      outputPresentation?.showClientNamesInHeader ?? true,
  };
  // Refresh the approvals so that the DRAFT watermark goes away after the
  // presentation is attested or approved.
  newContextData.approvalKinds = uniq(
    (outputPresentation?.approvals ?? []).map(({ kind }) => kind)
  );
  newContextData.reviewRequirement =
    outputPresentation?.reviewRequirement ?? null;

  newContextData.professionalTeam = mapDataToTable([
    ...getNodes(data.professionalTeam),
    ...getNodes(data.professionalTeamOrganizations),
  ]);

  // Refresh cash flows so the assumptions slide's cash flow table reflects
  // edits made from it (the slide reads contextData.cashflows, which is
  // otherwise only seeded on the initial load). Liabilities aren't refreshed
  // here — they're read from the top-level context field that tracks the main
  // query, which the edit-liability mutation refetches.
  newContextData.cashflows = getNodes(data.cashflows);

  // Refresh the live entity list so entity-name edits propagate to the surfaces
  // that read the name from context (the designer sidebar tree and the table of
  // contents) rather than from the load-time `bundle.displayName` snapshot.
  newContextData.entities = getNodes(data.entities);

  newContextData.documentSummaryMap = getNodes(
    outputPresentation?.bundles
  ).reduce<ClientPresentationDesignerContextDataShape['documentSummaryMap']>(
    (acc, bundle) => {
      getNodes(bundle.pages)
        .filter(({ kind }) => kind === PresentationPageKind.DocumentSummaryPage)
        .forEach(({ documentSummaryPage }) => {
          const summaryId = documentSummaryPage?.summary?.id;
          if (documentSummaryPage && summaryId && !acc[summaryId]) {
            acc[summaryId] = documentSummaryPage;
          }
        });
      return acc;
    },
    {}
  );

  // check each bundle in the response...
  getNodes(outputPresentation?.bundles).forEach((bundle, index) => {
    const mapKey = resolveContextMapKey({
      responseBundle: bundle,
      index,
      bundles,
    });
    if (!mapKey) {
      return;
    }

    // update the EW bundle map
    const estateWaterfall = getWaterfallWithProjections(
      bundle.waterfallBundleConfiguration
    );
    if (estateWaterfall) {
      contextData.estateWaterfallBundleConfigurationMap[mapKey] =
        estateWaterfall;
    }

    // update the beneficiary report bundle map
    if (bundle.beneficiariesBundleConfiguration) {
      const estateWaterfall =
        bundle.beneficiariesBundleConfiguration?.estateWaterfall;
      const beneficiaryReport =
        bundle.beneficiariesBundleConfiguration
          ?.estateWaterfallBeneficiaryReport;

      if (estateWaterfall) {
        contextData.beneficiaryReportBundleWaterfallConfigurationMap[mapKey] =
          estateWaterfall;
      }

      if (beneficiaryReport) {
        contextData.beneficiaryReportConfigurationMap[mapKey] =
          beneficiaryReport;
      }
    }

    // update the entity summary map
    if (bundle.entitySummaryBundleConfiguration?.entity) {
      newContextData.entityDetailMap = updateEntityDetailMap(
        newContextData.entityDetailMap,
        mapKey,
        {
          ...bundle.entitySummaryBundleConfiguration.entity,
          deathOrder: bundle.entitySummaryBundleConfiguration.deathOrder,
        }
      );
    }

    // update the waterfall comparison map
    if (bundle.waterfallComparisonBundleConfiguration?.comparedWaterfalls) {
      contextData.waterfallComparisonBundleConfigurationMap[mapKey] =
        bundle.waterfallComparisonBundleConfiguration.comparedWaterfalls;
    }

    if (bundle.entityGraphViewBundleConfiguration?.entityGraphView) {
      contextData.entityGraphViewMap[mapKey] =
        bundle.entityGraphViewBundleConfiguration.entityGraphView;
    }

    if (bundle.entitiesOverviewBundleConfiguration?.householdMetrics) {
      contextData.entitiesOverviewMetricsMap[mapKey] =
        bundle.entitiesOverviewBundleConfiguration.householdMetrics;
    }

    // Refresh the All Entities slide's row entities so an entity edit is
    // reflected in the list (it reads these from context, not off the bundle).
    if (bundle.entitiesOverviewBundleConfiguration) {
      contextData.entitiesOverviewEntitiesMap[mapKey] = getNodes(
        bundle.entitiesOverviewBundleConfiguration.entities
      );
    }
  });

  // Hand the map a fresh reference so consumers that memoize on it (the All
  // Entities slide) recompute — the loop above mutates the entries in place,
  // which wouldn't otherwise change the map identity.
  newContextData.entitiesOverviewEntitiesMap = {
    ...contextData.entitiesOverviewEntitiesMap,
  };

  setContextData(newContextData);
}

export function useUpdateContextData(
  shouldTriggerGlobalLoadAnimation = false
): {
  updateContextData: () => Promise<UpdateContextDataOutput | null>;
  isUpdateRunning: boolean;
} {
  const {
    presentationId,
    householdId,
    contextData,
    setContextData,
    isPersistRunning,
    setPersistRunning,
  } = useClientPresentationDesignerContext();
  const { reportError } = useReportError();
  const strategy = usePresentationSaveStrategy();
  const [refetch] = useClientPresentationDesignerV2LazyQuery({
    fetchPolicy: 'no-cache', // cache normalization breaks across the various page types
    variables: {
      presentationId,
      householdId,
      presentationOnly: true,
      entitiesWhere: ENTITIES_WHERE_VARIABLE,
    },
  });
  const { control } = useFormContext<ClientPresentationShape>();
  const [isLocalPersistRunning, setIsLocalPersistRunning] = useState(false);

  const [bundles] = useWatch({
    control,
    name: ['bundles', 'presentationConfiguration', 'title'],
  });

  const setPersist = shouldTriggerGlobalLoadAnimation
    ? setPersistRunning
    : setIsLocalPersistRunning;

  const updateContextDataCb = useCallback(async () => {
    setPersist(true);
    let result: UpdateContextDataOutput | null = null;
    try {
      result = await refetch();
      if (result.data) {
        // This caller only refreshes context data — it performs no write, so no
        // id has changed and the form bundles are already current. That is what
        // makes either resolver correct here: every response bundle is one the
        // form already knows, by id and by position. See T1-6071.
        rebuildContextData({
          data: result.data,
          contextData,
          setContextData,
          bundles,
          resolveContextMapKey: strategy.resolveContextMapKey,
        });
      }
    } catch (err) {
      reportError('Failed to update context data', err as Error);
      return null;
    } finally {
      setPersist(false);
    }
    return result;
  }, [
    bundles,
    contextData,
    refetch,
    reportError,
    setContextData,
    setPersist,
    strategy,
  ]);

  return {
    updateContextData: updateContextDataCb,
    isUpdateRunning: isPersistRunning || isLocalPersistRunning,
  };
}
